import Clickable from "components/Clickable";
import { ButtonPrimary } from "components/Clickable/Buttons";
import { Dropdown, DropdownItem } from "components/Dropdown/Dropdown";
import { ErrorMessage } from "components/Input/ErrorMessage";
import { PasswordInputGroup, TextInputGroup } from "components/Input/Input";
import { writeCMSDataClientSide } from "helpers/api";
import { basicEmailPattern, processServerSideErrors } from "helpers/forms";
import urls from "helpers/urls";
import { FormBase } from "interfaces";
import Link from "next/link";
import { useRouter } from "next/router";
import styles from "pages/account/auth/account-auth.module.scss";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { profileDescriptionMap } from "./interfaces";

interface Form extends FormBase {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    description: string;
    corporate_account?: {
        account_number: string;
    };
}

export const RegisterForm = () => {
    const descriptionPlaceholder = "Select an option";
    const {
        clearErrors,
        control,
        formState: { errors, isSubmitting },
        handleSubmit,
        register,
        setError,
        watch,
    } = useForm<Form>();
    const router = useRouter();

    const onSubmit: SubmitHandler<Form> = async (data) => {
        if (!data.corporate_account?.account_number) {
            delete data.corporate_account;
        }
        const response = await writeCMSDataClientSide(
            "POST",
            `/api/auth/register`,
            {},
            data,
        );
        if (response.ok) {
            if (router.query.next || router.asPath === urls.dealerSignIn) {
                window.location.href = `${
                    router.query.next || urls.dealerSignIn
                }`;
            } else {
                window.location.pathname = urls.accountOverview;
            }
        } else {
            processServerSideErrors(response, data, setError);
        }
    };

    return (
        <>
            <h2 className="h4">Create an Account</h2>
            <form
                noValidate
                onSubmit={(event) => {
                    /**
                     * Server-side errors need cleared before you can submit again
                     * https://github.com/react-hook-form/react-hook-form/issues/70
                     */
                    clearErrors();
                    handleSubmit(onSubmit)(event);
                }}
                className={styles.form}
            >
                <TextInputGroup
                    {...register("first_name", {
                        required: {
                            value: true,
                            message: "Enter your first name.",
                        },
                    })}
                    type="text"
                    label={"First Name"}
                    errorMessage={errors.first_name?.message}
                />
                <TextInputGroup
                    {...register("last_name", {
                        required: {
                            value: true,
                            message: "Enter your last name.",
                        },
                    })}
                    type="text"
                    label={"Last Name"}
                    errorMessage={errors.last_name?.message}
                />
                <TextInputGroup
                    {...register("email", {
                        required: {
                            value: true,
                            message: "Enter your email address.",
                        },
                        pattern: basicEmailPattern,
                    })}
                    type="email"
                    label={"Email"}
                    errorMessage={errors.email?.message}
                />
                <PasswordInputGroup
                    {...register("password", {
                        required: { value: true, message: "Enter a password." },
                    })}
                    errorMessage={errors.password?.message}
                />
                <Controller
                    name="description"
                    control={control}
                    defaultValue={descriptionPlaceholder}
                    render={({ field: { onChange, value } }) => (
                        <Dropdown
                            className={styles.userDescription}
                            label="Describe Yourself"
                            labelSize="small"
                            placeholder={descriptionPlaceholder}
                            name="description"
                            value={value}
                            onChange={onChange}
                            errorMessage={errors.description?.message}
                        >
                            {Object.entries(profileDescriptionMap).map(
                                ([key, value]) => (
                                    <DropdownItem key={key} id={key}>
                                        {value}
                                    </DropdownItem>
                                ),
                            )}
                        </Dropdown>
                    )}
                />
                {watch("description") === "DE" && (
                    <TextInputGroup
                        {...register("corporate_account.account_number")}
                        type="text"
                        label="Emtek Account ID (optional)"
                        // See source of processServerSideErrors for explanation why this cast to `any` is required
                        errorMessage={
                            (errors.corporate_account as any)?.message
                                .account_number
                        }
                    />
                )}

                <ButtonPrimary
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                >
                    Register
                </ButtonPrimary>
            </form>

            {errors.server?.message && (
                <ErrorMessage>{errors.server.message}</ErrorMessage>
            )}

            <p className={styles.footerCopy}>
                By creating an account you are agreeing to our{" "}
                <Clickable unstyled={true} href={urls.terms} target="_blank">
                    Terms of Use
                </Clickable>{" "}
                and{" "}
                <Clickable unstyled={true} href={urls.privacy} target="_blank">
                    Privacy Policy
                </Clickable>
            </p>
            <div className={styles.footerLinks}>
                <Link href={urls.signIn}>Already have an account?</Link>
            </div>
        </>
    );
};
